import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, RefreshCw } from "lucide-react";
import { errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import useAdminApi from "@/hooks/useAdminApi";
import StatusBadge from "@/components/StatusBadge";

const FILTERS = [["", "All"], ["cancel_requested", "Cancel requests"], ["awaiting_quote", "Needs rate"], ["pending_verification", "To verify"], ["pending_payment", "Awaiting payment"], ["paid", "Paid"], ["failed", "Failed"]];

const QuoteForm = ({ b, update }) => {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-2">
      <input type="number" min={1} placeholder="Total ₹" className="field !h-8 !px-2 w-28" value={v} onChange={(e) => setV(e.target.value)} data-testid={`admin-quote-input-${b.booking_id}`} />
      <button disabled={!(+v > 0)} onClick={() => update(b, { quote_total: +v })} className="btn-primary !h-8 !px-3 !text-[10px]" data-testid={`admin-send-quote-${b.booking_id}`}>Send rate</button>
    </div>
  );
};

const waLink = (b) => {
  let d = (b.guest.phone || "").replace(/\D/g, "").replace(/^0+/, "");
  if (d.length === 10) d = `91${d}`;
  const stale = ["expired", "failed"].includes(b.payment_status);
  const amount = stale ? "We noticed your payment was not completed. We would be happy to help you rebook or complete your reservation." : b.total != null ? `The amount of ${inr(b.total)} is pending.` : "We are confirming your room rate.";
  const link = stale ? `${window.location.origin}/book?property=${b.property_slug}` : `${window.location.origin}/manage-booking?id=${b.booking_id}`;
  const msg = `Hello ${b.guest.name}, this is The Ananta Hotels regarding your booking ${b.booking_id} at ${b.property_name} (${fmtDate(b.check_in)} to ${fmtDate(b.check_out)}). ${amount} ${stale ? "Book again here" : "You can view and pay for your booking here"}: ${link}`;
  return `https://wa.me/${d}?text=${encodeURIComponent(msg)}`;
};
const UNPAID = ["pending_payment", "awaiting_quote", "failed", "expired"];

const Actions = ({ b, update }) => (
  <div className="flex flex-wrap gap-2">
    {b.payment_status === "awaiting_quote" && b.status !== "cancelled" && <QuoteForm b={b} update={update} />}
    {!['paid', 'awaiting_quote'].includes(b.payment_status) && b.status !== "cancelled" && <button onClick={() => update(b, { payment_status: "paid" })} className="btn-primary !h-8 !px-3 !text-[10px]" data-testid={`admin-mark-paid-${b.booking_id}`}>Mark paid</button>}
    {b.payment_status === "pending_verification" && <button onClick={() => update(b, { payment_status: "failed" })} className="btn-ghost !h-8 !px-3 !text-[10px] text-red-700" data-testid={`admin-mark-failed-${b.booking_id}`}>Not received</button>}
    {(b.payment_status === "paid" || (b.status === "cancelled" && (b.cancel_request || b.payment_status === "pending_verification"))) && b.payment_status !== "refunded" && <button onClick={() => update(b, { payment_status: "refunded" })} className="btn-ghost !h-8 !px-3 !text-[10px] text-[#6b6b6b]" data-testid={`admin-mark-refunded-${b.booking_id}`}>Refunded</button>}
    {b.status !== "cancelled" && <button onClick={() => window.confirm(`Cancel booking ${b.booking_id}?`) && update(b, { status: "cancelled" })} className="btn-ghost !h-8 !px-3 !text-[10px] text-red-700" data-testid={`admin-cancel-${b.booking_id}`}>Cancel</button>}
  </div>
);

export default function BookingsTab() {
  const call = useAdminApi();
  const [filter, setFilter] = useState("");
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    call("get", `/admin/bookings${filter ? `?payment_status=${filter}` : ""}`).then(setItems).catch((e) => toast.error(errMsg(e)));
  }, [call, filter]);
  useEffect(() => {
    load();
  }, [load]);

  const update = async (b, body) => {
    try {
      await call("patch", `/admin/bookings/${b.booking_id}`, body);
      toast.success(`Booking ${b.booking_id} updated`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-5">
        {FILTERS.map(([v, l]) => <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v || "all"}`} className={`h-9 px-4 text-xs uppercase tracking-[0.15em] border ${filter === v ? "bg-[#1E3B2E] text-white border-[#1E3B2E]" : "bg-white border-[#E2DCD0]"}`}>{l}</button>)}
        <button onClick={load} className="ml-auto h-9 w-9 flex items-center justify-center border border-[#E2DCD0] bg-white" aria-label="Refresh" data-testid="admin-bookings-refresh"><RefreshCw size={14} /></button>
      </div>
      {items?.length === 0 && <p className="text-sm text-[#6b6b6b]" data-testid="admin-bookings-empty">No bookings yet.</p>}
      <div className="space-y-3" data-testid="admin-bookings-list">
        {items?.map((b) => (
          <div key={b.booking_id} className="bg-white border border-[#E2DCD0] p-5 grid gap-4 md:grid-cols-12 items-start" data-testid={`admin-booking-${b.booking_id}`}>
            <div className="md:col-span-3">
              <p className="font-medium tracking-wider">{b.booking_id}</p>
              <p className="text-xs text-[#6b6b6b]">{b.property_name}</p>
              <div className="mt-2 flex flex-wrap gap-2"><StatusBadge status={b.payment_status} testId={`admin-booking-pay-status-${b.booking_id}`} />{b.status === "cancelled" && <StatusBadge status="cancelled" />}</div>
            </div>
            <div className="md:col-span-3 text-sm">
              <p>{b.guest.name}</p>
              <p className="text-xs text-[#6b6b6b] break-all">{b.guest.email}</p>
              <p className="text-xs text-[#6b6b6b]">{b.guest.phone}</p>
              {UNPAID.includes(b.payment_status) && b.status !== "cancelled" && (
                <a href={waLink(b)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-2 h-8 px-3 bg-[#25D366] text-white text-[10px] uppercase tracking-[0.15em] font-medium hover:bg-[#1da851] transition-colors" data-testid={`admin-whatsapp-${b.booking_id}`}>
                  <MessageCircle size={13} />WhatsApp guest
                </a>
              )}
            </div>
            <div className="md:col-span-3 text-sm">
              <p>{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</p>
              <p className="text-xs text-[#6b6b6b]">{b.rooms.map((r) => `${r.name} ×${r.qty}`).join(", ")}</p>
              <p className="text-xs text-[#6b6b6b]">{b.adults} adults, {b.children} children</p>
            </div>
            <div className="md:col-span-3 space-y-2">
              <p className="font-['Cormorant_Garamond'] text-2xl">{b.total == null ? "Rate to confirm" : inr(b.total)}</p>
              {b.utr && <p className="text-xs">UTR: <strong data-testid={`admin-booking-utr-${b.booking_id}`}>{b.utr}</strong></p>}
              {b.cancel_request && b.payment_status !== "refunded" && <p className="text-xs text-red-700 font-medium" data-testid={`admin-cancel-request-${b.booking_id}`}>Cancellation requested · refund due {inr(b.cancel_request.refund_amount)}{b.cancel_request.reason ? ` · "${b.cancel_request.reason}"` : ""}</p>}
              <Actions b={b} update={update} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
